import { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import WhoAmIContext from '../../services/whoami';

/**
 * FeatureGate component that controls access to features based on entitlements
 * 
 * @param {string} featureName - The name of the feature to check (e.g., 'bulkUploadEnabled')
 * @param {ReactNode} children - The content to render if feature is enabled
 * @param {ReactNode} fallback - The component to render if feature is disabled (default: redirect to home)
 */
const FeatureGate = ({ featureName, children, fallback = <Navigate to="/" replace />, refreshOnMount = false }) => {
  const context = useContext(WhoAmIContext);
  const [verified, setVerified] = useState(!refreshOnMount);

  useEffect(() => {
    if (!refreshOnMount || !context?.refreshWhoAmI) return;
    let active = true;
    setVerified(false);
    context.refreshWhoAmI().finally(() => {
      if (active) setVerified(true);
    });
    return () => { active = false; };
  }, [refreshOnMount, context?.refreshWhoAmI]);

  if (!context) {
    return fallback;
  }

  // Wait for whoami to load
  if (!context.loaded || !verified) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (context[featureName]) {
    return children;
  }

  return fallback;
};

export default FeatureGate;
